<template>
  <section class="section">
    <div class="columns is-centered">
      <div class="column is-full-width">
        <h3 class="title is-4 is-spaced">
          <span>{{ $t('shippingCosts.title') }}</span>
        </h3>
        <div class="field">
          <label class="label">{{ $t('shippingCosts.texts.timeToPrepareTitle') }}</label>
          <span v-html="$t('shippingCosts.texts.timeToPrepare', { days: view.timeToPrepare })"></span>
        </div>
        <hr>
        <div class="field">
          <label class="label">{{ $t('shippingCosts.fields.shippingMode') }}</label>
          <div class="control has-icons-left">
            <div class="select">
              <select v-model="shippingMode">
                <option value="unique">{{ $t('shippingCosts.fields.modeUnique') }}</option>
                <option value="perCountry">{{ $t('shippingCosts.fields.modePerCountry') }}</option>
              </select>
            </div>
              <span class="icon is-small is-left">
                <i class="fa fa-truck"></i>
              </span>
          </div>
        </div>
        <div class="field" v-for="message in view.messages" :key="message.id">
          <success-notification :onClosed="() => message.message = ''" v-if="message.type === 'success' && message.message">{{ message.message }}</success-notification>
          <failure-notification :onClosed="() => message.message = ''" v-if="message.type === 'failure' && message.message">{{ message.message }}</failure-notification>
          <warning-notification :onClosed="() => message.message = ''" v-if="message.type === 'warning' && message.message">{{ message.message }}</warning-notification>
        </div>
        <div class="field">
          <p class="help is-size-6">{{ $t('shippingCosts.texts.optionalBands', { maxWeight: maxOrderWeightKg }) }}</p>
        </div>
        <div class="field table-container">
          <table class="table is-fullwidth">
            <thead>
              <tr>
                <th>{{ $t('shippingCosts.texts.saleCountry') }}</th>
                <th v-for="band in bands" :key="band.field" class="band-column">
                  {{ $t('shippingCosts.texts.' + band.field) }}
                  <span v-if="!band.required" class="has-text-grey is-size-7 has-text-weight-normal">
                    <br>{{ $t('shippingCosts.texts.optional') }}
                  </span>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="country in filteredCountries" :key="country.id" :class="{ 'draft': hasInformationChanged(country) }">
                <td :class="{'has-text-weight-bold': country.id === 0}">
                  {{ country.countryName }}
                </td>
                <td v-for="band in bands" :key="band.field">
                  <money-input v-model="country.shippings[band.field]"></money-input>
                </td>
                <td>
                  <div class="level">
                    <div class="level-item">
                      <div class="buttons">
                        <publish-button v-if="!isPublished(country)" :disabled="!allFieldsFilled(country)" :label="$t('app.buttons.publish')" @click="addCountry(country)" :loading="country.loading"></publish-button>
                        <unpublish-button v-if="isPublished(country)" :label="$t('app.buttons.unpublish')" @click="removeCountry(country)" :loading="country.loading"></unpublish-button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <hr>
        <div class="field">
          <label class="label">{{ $t('shippingCosts.containers.title') }}</label>
          <span>{{ $t('shippingCosts.containers.description') }}</span>
          <table class="table is-fullwidth" v-if="view.containers && view.containers.length > 0">
            <thead>
              <tr>
                <th>{{ $t('shippingCosts.containers.table.name') }}</th>
                <th>{{ $t('shippingCosts.containers.table.height') }}</th>
                <th>{{ $t('shippingCosts.containers.table.length') }}</th>
                <th>{{ $t('shippingCosts.containers.table.width') }}</th>
                <th>{{ $t('shippingCosts.containers.table.maxWeight') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="container in view.containers" :key="container.id">
                <td>{{ container.name }}</td>
                <td>{{ container.height }}</td>
                <td>{{ container.length }}</td>
                <td>{{ container.width }}</td>
                <td>{{ container.maxWeight || '-' }}</td>
                <td>
                  <button class="button is-small is-danger" @click="removeContainer(container)" :disabled="container.loading">
                    <span class="icon is-small">
                      <i class="fa fa-trash"></i>
                    </span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="field is-grouped" style="margin-top: 1rem;">
            <div class="control">
              <input class="input" type="text" v-model="newContainer.name" :placeholder="$t('shippingCosts.containers.table.name')">
            </div>
            <div class="control">
              <input class="input" type="number" step="0.01" v-model.number="newContainer.height" :placeholder="$t('shippingCosts.containers.table.height')">
            </div>
            <div class="control">
              <input class="input" type="number" step="0.01" v-model.number="newContainer.length" :placeholder="$t('shippingCosts.containers.table.length')">
            </div>
            <div class="control">
              <input class="input" type="number" step="0.01" v-model.number="newContainer.width" :placeholder="$t('shippingCosts.containers.table.width')">
            </div>
            <div class="control">
              <input class="input" type="number" step="0.01" v-model.number="newContainer.maxWeight" :placeholder="$t('shippingCosts.containers.table.maxWeight')">
            </div>
            <div class="control">
              <add-button @click="addContainer" :disabled="!canAddContainer"></add-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { FooshiApi } from '@/services/fooshiApi.service'

import MoneyInput from '../../components/forms/MoneyInput.vue'
import SuccessNotification from '../../components/SuccessNotification.vue'
import FailureNotification from '../../components/FailureNotification.vue'
import WarningNotification from '../../components/WarningNotification.vue'
import PublishButton from '../../components/forms/PublishButton.vue'
import UnpublishButton from '../../components/forms/UnpublishButton.vue'
import AddButton from '../../components/forms/AddButton.vue'

// Mirrors WEIGHT_BANDS in the API's shipping.service.js. Bands above 50 kg are
// optional: baskets in an empty band cannot be ordered.
const BANDS = [
  { field: 'rangeA', required: true },
  { field: 'rangeB', required: true },
  { field: 'rangeC', required: true },
  { field: 'rangeD', required: true },
  { field: 'rangeE', required: true },
  { field: 'rangeF', required: true },
  { field: 'rangeG', required: false },
  { field: 'rangeH', required: false },
  { field: 'rangeI', required: false },
  { field: 'rangeJ', required: false }
]
const REQUIRED_BANDS = BANDS.filter(band => band.required)
const MAX_ORDER_WEIGHT_KG = 150

const isBlank = (value) => value === null || value === undefined || value === ''

export default {
  components: {
    MoneyInput,
    SuccessNotification,
    FailureNotification,
    WarningNotification,
    PublishButton,
    UnpublishButton,
    AddButton
  },
  name: 'ShippingMethods',
  methods: {
    hasInformationChanged (country) {
      if (!country.shippings) {
        return false
      }

      return BANDS.some(({ field }) => {
        const current = country.shippings[field]
        const saved = country._backEntity[field]
        if (isBlank(current) && isBlank(saved)) {
          return false
        }
        return current !== saved
      })
    },
    isPublished (country) {
      return !this.hasInformationChanged(country) && Boolean(country.shippings) &&
        REQUIRED_BANDS.every(({ field }) => country._backEntity[field])
    },
    allFieldsFilled (country) {
      return REQUIRED_BANDS.every(({ field }) => country.shippings[field])
    },
    async addCountry (country) {
      // console.log(country)
      // if eu is set then first unpublish eu first
      // if (Object.keys(this.view.countries[0].shippings).length > 0) {
      //   this.removeCountry(this.view.countries[0])
      // }
      // if other countries are set and adding eu then disable other countries first
      // if (country.id === 0) {
      // for (const item of this.view.countries) {
      //   this.removeCountry(item)
      // }
      // }
      country.loading = true
      var shipping = {
        country: country.id
      }
      BANDS.forEach(({ field }) => {
        const value = country.shippings[field]
        shipping[field] = isBlank(value) ? null : value
      })

      const response = await FooshiApi.addVendorShippingMethods([
        shipping
      ])

      if (response.success) {
        country._backEntity = shipping
        this.pushMessage('success', this.$t('shippingCosts.texts.added', {
          countryName: country.countryName
        }))

        if (country.id === 0) {
          for (const item of this.view.countries) {
            this.resetCountryFields(item)
          }
          await this.fetchData()
        }
      } else {
        this.handleApiError(response)
      }
      country.loading = false
    },
    resetCountryFields (country) {
      BANDS.forEach(({ field }) => {
        this.$set(country.shippings, field, '')
        this.$set(country._backEntity, field, '')
      })
    },
    async removeCountry (country) {
      country.loading = true
      const response = await FooshiApi.deactivateVendorShippingMethods(country.id)
      if (response.success) {
        this.resetCountryFields(country)
        this.pushMessage('warning', this.$t('shippingCosts.texts.removed', {
          countryName: country.countryName
        }))
        country.loading = false
      } else {
        this.handleApiError(response)
      }
    },
    handleApiError (response) {
      if (Array.isArray(response.message)) {
        response.message.map(e => {
          this.pushMessage('failure', this.$t(e))
        })
      } else {
        this.pushMessage('failure', this.$t(response.message))
      }
    },
    pushMessage (type, message) {
      this.view.messages.push({
        message,
        type
      })
    },
    filterCountries () {
      if (this.shippingMode === 'unique') {
        this.filteredCountries = [this.view.countries[0]]
      } else if (this.shippingMode === 'perCountry') {
        this.filteredCountries = this.view.countries.slice(1)
      } else {
        this.filteredCountries = this.view.countries
      }
    },
    async fetchData () {
      const countriesResponse = await FooshiApi.getVendorShippingMethods()
      if (countriesResponse.success) {
        this.view.countries = []
        this.view.timeToPrepare = countriesResponse.data.shipTime

        var europeanSetup = {}
        countriesResponse.data.countries.forEach(country => {
          var setup = {
            id: country.id,
            countryName: country.countryName,
            shippings: {},
            loading: false,
            _backEntity: {}
          }

          if (country.shippings && country.shippings.length > 0) {
            setup._backEntity = country.shippings[0]
            BANDS.forEach(({ field }) => {
              setup.shippings[field] = country.shippings[0][field]
            })
          }

          if (setup.id === 0) {
            europeanSetup = setup
          } else {
            this.view.countries.push(setup)
          }
        })
        this.view.countries.sort((a, b) => {
          return a.countryName.localeCompare(b.countryName)
        })
        // add europe first
        this.view.countries = [europeanSetup, ...this.view.countries]

        // Set default mode logic
        if (Object.keys(this.view.countries[0].shippings).length > 0) {
          this.shippingMode = 'unique'
        } else {
          this.shippingMode = 'perCountry'
        }
      }
      await this.fetchContainers()
    },
    async fetchContainers () {
      const response = await FooshiApi.getContainers()
      if (response.success) {
        this.view.containers = response.data.map(container => ({
          ...container,
          loading: false
        }))
      }
    },
    async addContainer () {
      if (!this.canAddContainer) {
        return
      }

      const container = {
        name: this.newContainer.name,
        height: this.newContainer.height,
        length: this.newContainer.length,
        width: this.newContainer.width,
        maxWeight: this.newContainer.maxWeight || null
      }

      const response = await FooshiApi.addContainer(container)
      if (response.success) {
        this.pushMessage('success', this.$t('shippingCosts.containers.added'))
        this.resetNewContainer()
        await this.fetchContainers()
      } else {
        this.pushMessage('failure', this.$t('shippingCosts.containers.error'))
        this.handleApiError(response)
      }
    },
    async removeContainer (container) {
      container.loading = true
      const response = await FooshiApi.deleteContainer(container.id)
      if (response.success) {
        this.pushMessage('success', this.$t('shippingCosts.containers.removed'))
        await this.fetchContainers()
      } else {
        this.pushMessage('failure', this.$t('shippingCosts.containers.error'))
        this.handleApiError(response)
      }
      container.loading = false
    },
    resetNewContainer () {
      this.newContainer.name = ''
      this.newContainer.height = null
      this.newContainer.length = null
      this.newContainer.width = null
      this.newContainer.maxWeight = null
    }
  },
  async mounted () {
    await this.fetchData()
  },
  computed: {
    bands () {
      return BANDS
    },
    maxOrderWeightKg () {
      return MAX_ORDER_WEIGHT_KG
    },
    messages () {
      return this.view.messages
    },
    canAddContainer () {
      return this.newContainer.name &&
        this.newContainer.height &&
        this.newContainer.length &&
        this.newContainer.width
    }
  },
  watch: {
    messages (oldVal, newVal) {
      if (newVal.length > 0) {
        setTimeout(() => this.view.messages.shift(), 4000)
      }
    },
    shippingMode () {
      this.filterCountries()
    }
  },
  data () {
    return {
      view: {
        countries: [],
        messages: [],
        containers: []
      },
      shippingMethod: {
        timeToPrepare: '',
        countrySetup: [{
          country: '',
          _0_5KG: '',
          _5_10KG: '',
          _10_20KG: '',
          _20_30KG: '',
          _30_40KG: '',
          _50KGOrHigher: ''
        }]
      },
      shippingMode: null,
      filteredCountries: [],
      newContainer: {
        name: '',
        height: null,
        length: null,
        width: null,
        maxWeight: null
      }
    }
  }
}
</script>
<style>
.draft {
  background-color: #F0F8FF
}
.band-column {
  min-width: 7.5rem;
}
</style>



// WEBPACK FOOTER //
// src/views/shipping/ShippingMethods.vue